import { ChevronDown, ChevronLeft, ChevronRight, ChevronUp } from 'lucide-react'
import type { KeyboardEvent, PointerEvent as ReactPointerEvent } from 'react'

import type { PtzDirection } from './use-camera-ptz'
import { m } from '@/lib/paraglide/messages'
import { twJoin } from '@/lib/utils'

interface Props {
    /** null = không có camera nào đang quay. */
    holding: PtzDirection | null
    onStart: (direction: PtzDirection) => void
    onStop: () => void
    /** Camera chỉ xem: nút vẫn hiện nhưng khoá, kèm lời giải thích. */
    disabled?: boolean
}

const DIRECTIONS: Array<{
    direction: PtzDirection
    label: () => string
    Icon: typeof ChevronUp
    className: string
}> = [
    {
        direction: 'len',
        label: () => m.grower_area_camera_ptz_up(),
        Icon: ChevronUp,
        className: 'col-start-2 row-start-1',
    },
    {
        direction: 'trai',
        label: () => m.grower_area_camera_ptz_left(),
        Icon: ChevronLeft,
        className: 'col-start-1 row-start-2',
    },
    {
        direction: 'phai',
        label: () => m.grower_area_camera_ptz_right(),
        Icon: ChevronRight,
        className: 'col-start-3 row-start-2',
    },
    {
        direction: 'xuong',
        label: () => m.grower_area_camera_ptz_down(),
        Icon: ChevronDown,
        className: 'col-start-2 row-start-3',
    },
]

/**
 * Bàn điều khiển 4 hướng theo kiểu BẤM GIỮ, đúng hợp đồng của API camera:
 * nhấn là `bat_dau`, thả là `dung`.
 *
 * Mọi đường ra đều phải gọi `onStop`: thả chuột, kéo con trỏ ra khỏi nút, huỷ
 * cảm ứng, nhả phím, rời focus. Ô giữa chỉ là ĐÈN BÁO đang quay, không phải
 * nút — API không có lệnh "về giữa", thêm nút giả sẽ làm người dùng hiểu sai.
 */
export function CameraPtzPad({ holding, onStart, onStop, disabled }: Props) {
    const handlePointerDown =
        (direction: PtzDirection) => (event: ReactPointerEvent) => {
            if (disabled) return
            // Chặn hành vi mặc định (chọn chữ, kéo, giả lập chuột trên cảm
            // ứng) để lúc giữ nút không bị trôi mất pointerup.
            event.preventDefault()
            event.currentTarget.setPointerCapture?.(event.pointerId)
            onStart(direction)
        }

    const handleKeyDown =
        (direction: PtzDirection) => (event: KeyboardEvent) => {
            if (disabled) return
            // Bỏ qua auto-repeat của hệ điều hành: giữ phím không được bắn
            // `bat_dau` liên tục.
            if (event.repeat) return
            if (event.key !== ' ' && event.key !== 'Enter') return
            event.preventDefault()
            onStart(direction)
        }

    const handleKeyUp = (event: KeyboardEvent) => {
        if (event.key !== ' ' && event.key !== 'Enter') return
        onStop()
    }

    return (
        <div className="space-y-2">
            <div className="grid w-full max-w-[188px] grid-cols-3 grid-rows-3 gap-1.5">
                {DIRECTIONS.map(({ direction, label, Icon, className }) => (
                    <button
                        key={direction}
                        type="button"
                        aria-label={label()}
                        aria-pressed={holding === direction}
                        disabled={disabled}
                        data-testid={`ptz-${direction}`}
                        className={twJoin(
                            className,
                            'flex min-h-[48px] min-w-[44px] cursor-pointer items-center justify-center rounded-md border transition-colors',
                            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                            'disabled:cursor-not-allowed disabled:opacity-50',
                            holding === direction
                                ? 'border-primary bg-primary text-primary-foreground'
                                : 'border-input bg-card hover:border-primary hover:bg-accent',
                        )}
                        onPointerDown={handlePointerDown(direction)}
                        onPointerUp={onStop}
                        onPointerLeave={onStop}
                        onPointerCancel={onStop}
                        onKeyDown={handleKeyDown(direction)}
                        onKeyUp={handleKeyUp}
                        onBlur={onStop}
                    >
                        <Icon className="size-5" />
                    </button>
                ))}
                <span
                    aria-hidden
                    className="col-start-2 row-start-2 flex items-center justify-center"
                >
                    <span
                        data-testid="ptz-indicator"
                        data-active={holding ? 'true' : 'false'}
                        className={twJoin(
                            'size-2.5 rounded-full transition-colors',
                            holding ? 'bg-primary' : 'bg-border',
                        )}
                    />
                </span>
            </div>
            <p className="text-xs text-muted-foreground">
                {disabled
                    ? m.grower_area_camera_ptz_view_only_hint()
                    : m.grower_area_camera_ptz_hint()}
            </p>
        </div>
    )
}
